import React, { useState, useEffect, useRef } from "react";

interface ImageMagnifierProps {
  imageUrl: string;
  zoomFactor?: number;
}

export const CustomImageMagnifier: React.FC<ImageMagnifierProps> = ({ imageUrl, zoomFactor = 2 }) => {
  // State to manage magnifier visibility and position
  const [magnifierVisible, setMagnifierVisible] = useState<boolean>(false);
  const [magnifierPosition, setMagnifierPosition] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [magnifiedImagePosition, setMagnifiedImagePosition] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [magnifiedImageSize, setMagnifiedImageSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  // Refs for accessing DOM elements
  const imageContainerRef = useRef<HTMLDivElement | null>(null);
  const sourceImageRef = useRef<HTMLImageElement | null>(null);
  const magnifierRef = useRef<HTMLDivElement | null>(null);
  const updateMagnifierPosition = () => {
      const element = document.getElementById("imageContainer");
      if(element){
        setMagnifierPosition({
          left:element.offsetWidth + 10,
          top: 0
        })
      }
  };
  // Update magnifier position on mount and window resize
  useEffect(() => {
    updateMagnifierPosition();
    window.addEventListener("resize", updateMagnifierPosition);

    return () => window.removeEventListener("resize", updateMagnifierPosition);
  }, []);

  // Handle mouse move inside the image to show magnifier and adjust zoom
  const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    updateMagnifierPosition();
    if (!sourceImageRef.current || !magnifierRef.current) return;

    const rect = sourceImageRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Show the magnifier only when the mouse is over the image
    setMagnifierVisible(true);

    // Calculate magnified image position
    const width = sourceImageRef.current.offsetWidth * zoomFactor;
    const height = sourceImageRef.current.offsetHeight * zoomFactor;

    setMagnifiedImageSize({ width, height });
    setMagnifiedImagePosition({
      left: -x * zoomFactor + magnifierRef.current.offsetWidth / 2,
      top: -y * zoomFactor + magnifierRef.current.offsetHeight / 2,
    });
  };

  // Hide the magnifier when mouse leaves the image
  const handleMouseLeave = () => {
    setMagnifierVisible(false);
  };

  return (
    <div style={{ display: "flex", alignItems: "flex-start", position:"relative" , justifyContent:"center", border:"1px solid #FBF5EE", padding:"15px", borderRadius:"12px"}}>
      {/* Image Container */}
      <div
        id="imageContainer"
        ref={imageContainerRef}
        className="image-container"
        style={{
          position: "relative",
          display: "inline-block",
          cursor: "zoom-in"
        }}
      >
        <img
          id="sourceImage"
          ref={sourceImageRef}
          src={imageUrl}
          alt="Source"
          style={{ maxHeight: "450px", width: "auto", maxWidth: "100%" }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        />
      </div>

      {/* Magnifier */}
      <div
        id="magnifier"
        ref={magnifierRef}
        className="magnifier"
        style={{
          position: "absolute",
          display: magnifierVisible ? "block" : "none",
          top: `${magnifierPosition.top}px`,
          left: `${magnifierPosition.left}px`,
          width: "450px",
          height: "450px",
          overflow: "hidden",
          backgroundColor: "#ffffff"
        }}
      >
        <img
          id="magnifiedImage"
          src={imageUrl}
          alt="Magnified"
          style={{
            position: "absolute",
            width: `${magnifiedImageSize.width}px`,
            height: `${magnifiedImageSize.height}px`,
            maxHeight: `${magnifiedImageSize.height}px`,
            left: `${magnifiedImagePosition.left}px`,
            top: `${magnifiedImagePosition.top}px`,
          }}
        />
      </div>
    </div>
  );
};
